import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import { featureFlags } from '@/config/featureFlags'

import Login from '@/components/pages/Login'

import Observation from '@/components/pages/Observation';
import Intent from '@/components/pages/Intent';
import Language from '@/components/pages/Language';
import Device from '@/components/pages/Device';
import CarePlan from '@/components/pages/CarePlan';
import Upload from '@/components/pages/Upload';
import End from '@/components/pages/End';
import Treatment from '@/components/pages/Treatment';
import Triage from '@/components/pages/Triage';
import Panel from '@/components/pages/Panel';
import Track from '@/components/pages/Track';
import Start from '@/components/pages/Start';
import Summary from '@/components/pages/Summary';
import Preference from '@/components/pages/Preference';
import Location from '@/components/pages/Location';
import StreamlinedOption from '@/components/pages/StreamlinedOption';
import Encounters from '@/components/pages/Encounters';
import UserInterface from '@/components/pages/UserInterface';
import PastPatientVisits from '@/components/pages/PastPatientVisits';

Vue.use(VueRouter)

const routes = [
    {
        path: '/encounters',
        component: Encounters,
        name: 'Encounters',
        meta: {
            position: 'center',
            show: false,
        }
    },
    {
        path: '/language',
        component: Language,
        name: 'Language',
        meta: {
            position: 'center'
        }
    },
    {
        path: '/location*',
        component: Location,
        name: 'Location',
        meta: {
            position: 'center'
        }
    },
    {
        path: '/device',
        component: Device,
        name: 'Device',
        meta: {
            show: false,
            position: 'center'
        }
    }, {
        path: '/preference/user_interface',
        component: UserInterface,
        name: 'UserInterface',
        meta: {
            collection: 'preference',
            ready: false,
            position: 'center'
        }
    },
    {
        path: '/preference/:panels_name(summary)',
        component: Summary,
        name: 'Summary',
        meta: {
            collection: 'preference',
            ready: true,
            position: 'center'
        }
    },
    {
        path: '/preference/:panels_name',
        component: Preference,
        name: 'Preference',
        meta: {
            collection: 'preference',
            position: 'center'
        }
    },
    {
        path: '/track/:tracks_id/:panels_name(streamlined_options)',
        component: StreamlinedOption,
        name: 'StreamlinedOption',
        meta: {
            position: 'center',
        }
    },
    {
        path: '/track/:tracks_id/:panels_name(intent)',
        component: Intent,
        name: 'Intent',
        meta: {
            ready: true,
            show: false,
            top: false
        }
    }, {
        path: '/track/:tracks_id/:panels_name(summary)',
        component: Summary,
        name: 'Summary',
        meta: {
            collection: 'panel',
            ready: true,
            position: 'center'
        }
    }, {
        path: '/track/:tracks_id/:panels_name(end)',
        component: End,
        name: 'End',
        meta: {
            ready: true,
            show: false,
            position: 'center'
        }
    }, {
        path: '/track/:tracks_id/:panels_name(triage)',
        component: Triage,
        name: 'Triage',
        meta: {
            ready: true,
            position: 'center'
        }
    }, {
        path: '/track/:tracks_id/:panels_name(careplan)',
        component: CarePlan,
        name: 'Careplan',
        meta: {
            ready: true,
            position: 'center'
        }
    }, {
        path: '/track/:tracks_id/:panels_name(treatment)',
        component: Treatment,
        name: 'Treatment',
        meta: {
            ready: true,
            position: 'center'
        }
    }, {
        path: '/track/:tracks_id/:panels_name(upload)',
        component: Upload,
        name: 'Upload',
        meta: {
            show: false,
            position: 'center'
        }
    }, {
        path: '/track/:tracks_id/:panels_name(observation)',
        component: Observation,
        name: 'Observation',
        meta: {
            ready: true
        }
    }, {
        path: '/track/:tracks_id/:panels_name',
        component: Panel,
        name: 'Panel',
        meta: {
            collection: 'panel'
        }
    }, {
        path: '/track/',
        component: Track,
        name: 'Track',
        meta: {
            position: 'center'
        }
    }, {
        path: '/start/',
        component: Start,
        name: 'Start',
        meta: {
            ready: true,
            show: false
        }
    }, {
        path: '/past-visits',
        component: PastPatientVisits,
        name: 'PastPatientVisits',
        meta: {
            panel_name: 'past-visits',
            show: false,
            requiresPpv: true,
        }
    }, {
        path: '/',
        alias: '*',
        component: Login,
        name: 'Login',
        meta: {
            ready: true,
            show: false,
            top: false,
            sw: false,
            position: 'center'
        }
    }
]

const router = new VueRouter({ routes })

router.beforeEach((to, from, next) => {
    sendEventGA(to, from);

    if (!store.getters['v4/session/authenticated']) {
        if (to.name === 'Login') {
            next()
        } else {
            next({ name: 'Login' })
        }
    } else if (to.name === 'Login' && store.getters['v4/session/authenticated']) {
        next({ name: 'Start' })
    } else if (store.getters['v4/mode/get']('disableRouterGuards')) {
        next()
    } else if (/^\/track\//.test(to.path) && store.getters['v4/session/active'] === null) {
        next({ name: 'Start' })
    } else if (from.name === 'End' && (to.name === 'Upload' || to.path.includes('final_text'))) {
        next(false)
    } else if (from.path.includes('final_text') && to.name === 'Upload') {
        next(false)
    } else if (to.name === 'End' && (from.name === 'Start' || from.name === 'Track' || from.name === 'Device')) {
        next(false)
    } else if (to.name === 'Encounters' && /^\/track\//.test(from.path)) {
        next(false)
    } else if (to.meta.requiresPpv && !featureFlags.enablePPV) {
        next({ name: 'Start' })
    } else if (to.meta.collection === 'preference') {
        const locations = store.getters['v4/location/rootLocations'];
        const hasLocations = locations && locations.length > 0;
        const selectionsComplete = store.getters['v4/location/selectionsComplete'];
        if (hasLocations && !selectionsComplete) {
            next({ path: '/location' })
        } else {
            next()
        }
    }
    else {
        next()
    }
})

let panelTimer = null
let currentPanelName = null
let _trackFn = null
let _events = null
let beforeUnloadRegistered = false

function getPanelName(route) {
    return route.meta?.panel_name || route.params?.panels_name || route.name || route.path || 'unknown'
}

/**
 * Returns true if the route should be tracked (excludes anonymous/auth-only routes).
 * Centralizing this condition avoids inconsistency when adding new excluded routes.
 * @param {object} route - Vue Router route object
 * @returns {boolean}
 */
function isTrackableRoute(route) {
    return Boolean(route.name && route.name !== 'Login')
}

function getTrackFn() {
    if (!_trackFn) {
        try {
            const { track } = require('@/plugins/analytics')
            _trackFn = track || (() => {})
        } catch (e) {
            _trackFn = () => {}
        }
    }
    return _trackFn
}

function getEvents() {
    if (!_events) {
        try {
            _events = require('@/plugins/analytics/events').EVENTS
        } catch (e) {
            _events = {}
        }
    }
    return _events
}

/**
 * Tracks a panel exit event — used on route change and tab close.
 * @param {string} panelName - Name of the panel being exited
 * @param {string} routePath - Route path of the panel
 * @param {object} [extra={}] - Additional properties (e.g. exit_type)
 */
function trackPanelExit(panelName, routePath, extra = {}) {
    try {
        const events = getEvents()
        const durationSeconds = panelTimer ? panelTimer.getSeconds() : 0
        const track = getTrackFn()
        track(events.PANEL_EXIT, {
            panel_name: panelName,
            route_path: routePath,
            session_duration_seconds: durationSeconds,
            ...extra,
        })
    } catch (e) { }
}

/**
 * Tracks a panel view event — used on route change.
 * @param {string} panelName - Name of the panel being entered
 * @param {string} routePath - Route path of the panel
 * @param {string} source - Route name the user came from
 */
function trackPanelView(panelName, routePath, source) {
    const events = getEvents()
    const track = getTrackFn()
    track(events.PANEL_VIEW, {
        panel_name: panelName,
        route_path: routePath,
        source: source || 'direct',
    })
}

router.afterEach((to, from) => {
    if (to.fullPath === from.fullPath) return

    if (isTrackableRoute(from) && panelTimer) {
        trackPanelExit(getPanelName(from), from.path)
        panelTimer.stop()
        panelTimer = null
    }

    if (isTrackableRoute(to)) {
        const { createActiveSessionTimer } = require('@/plugins/analytics/helpers')
        panelTimer = createActiveSessionTimer()
        panelTimer.start()
        currentPanelName = getPanelName(to)
        trackPanelView(currentPanelName, to.path, from.name)
    }
})

if (typeof window !== 'undefined' && !beforeUnloadRegistered) {
    beforeUnloadRegistered = true
    window.addEventListener('beforeunload', () => {
        if (!panelTimer || !currentPanelName) return

        const mixpanelToken = (window?.thinkmd && window?.thinkmd?.mixpanel_token) || process.env.VUE_APP_MIXPANEL_TOKEN
        try {
            const { isAnalyticsEnabled } = require('@/plugins/analytics')
            if (!isAnalyticsEnabled || !isAnalyticsEnabled()) return
            const { sendBeaconEvent } = require('@/plugins/analytics/helpers')
            const durationSeconds = panelTimer.getSeconds()
            sendBeaconEvent(getEvents().PANEL_EXIT || 'panel_exit', {
                panel_name: currentPanelName,
                session_duration_seconds: durationSeconds,
                exit_type: 'tab_close',
            }, mixpanelToken)
        } catch (e) { }
    })
}


function sendEventGA(to, from) {
    const trackStore = store.getters["v4/track/current"];
    const trackName = trackStore.name || "";

    const startId = store.getters["v4/startId/current"] || "";
    const encounter = store.getters["v4/session/active"] || "";

    const engagementCode = (store.state && store.state.settings && store.state.settings.code) || "";

    const isMale = store.getters['v4/panel/checkResponse']('gender', 'male');
    const isFemale = store.getters['v4/panel/checkResponse']('gender', 'female') || ['prenatal', 'antenatal'].includes(trackStore.path);

    let encounterSite = ""

    try {
        let site_panel = store.getters['v4/preference/get']('encounter_site')
        if (site_panel.responses) {
            let site_panel_response = site_panel.responses.find(r => r.selected)
            if (site_panel_response) encounterSite = site_panel_response.value
        }
    } catch (e) {}

    let gender = ""
    if (isMale) {
        gender = 'male';
    } else if (isFemale) {
        gender = 'female';
    }

    const panel_name = from.params.panels_name || "";

    const panel = store.getters['v4/panel/get'](panel_name);
    const responseMap = (panel && panel.responses) ? panel.responses.filter(res => {
        return res.selected;
    }).map(res => {
        return res.value;
    }) : [];
    const responses = (responseMap.length > 0) ? responseMap.join(',') : "";

    const data = {
        encounter: encounter,
        track: trackName,
        engagementCode: engagementCode,
        gender: gender,
        panel: panel_name,
        panelResponse: responses,
        encounterSite: encounterSite,
        startId: startId
    }

    // HACK:
    // we dont know the dimension index for the engagement data custom dimension so we need to pull it from the variables.
    const dimensionIndex = {};
    dimensionIndex[`dimension${
            process.env.VUE_APP_GOOGLE_ANALYTICS_UA_DIMENSION_INDEX
        }`] = "engagementdata";

    Vue.$gtag.customMap(dimensionIndex);

    // make sure there is an engagement code, we want to capture all panels except non authenticated panels (i.e. login)
    if (Vue.$gtag && startId.length > 0) {
        Vue.$gtag.event("thinkmd_collection_event", { engagementdata: JSON.stringify(data) });
    }
}

export default router
